import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import { Calendar, Video, Clock, User, Edit, Trash2, AlertCircle } from 'lucide-react';
import { getAuthHeaders, getUser } from '../lib/auth';
import Navigation from '../components/Navigation';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MyMeetings = ({ embedded = false }) => {
  const navigate = useNavigate();
  const currentUser = getUser();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('upcoming'); // upcoming, past, cancelled

  useEffect(() => {
    loadMeetings();
    // Refresh meetings every 30 seconds
    const interval = setInterval(loadMeetings, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadMeetings = async () => {
    try {
      const response = await axios.get(`${API}/meetings/my-meetings`, {
        headers: getAuthHeaders()
      });
      setMeetings(response.data.meetings || []);
    } catch (error) {
      console.error('Failed to load meetings:', error);
      // Only show error toast if it's not just empty data
      if (error.response && error.response.status !== 404) {
        toast.error('Failed to load meetings. Please try again.');
      }
      setMeetings([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleCancelMeeting = async (meetingId) => {
    if (!window.confirm('Are you sure you want to cancel this meeting?')) {
      return;
    }

    try {
      await axios.post(`${API}/meetings/cancel`, {
        meetingId
      }, {
        headers: getAuthHeaders()
      });
      
      toast.success('Meeting cancelled successfully');
      await loadMeetings();
    } catch (error) {
      toast.error('Failed to cancel meeting');
    }
  };

  const handleRescheduleMeeting = (meetingId, hostId) => {
    navigate(`/meeting/reschedule/${meetingId}`);
  };

  const handleJoinMeeting = (roomId) => {
    navigate(`/meet/${roomId}`);
  };

  const filterMeetings = () => {
    const now = new Date();
    
    return meetings.filter(meeting => {
      const meetingTime = new Date(meeting.startUtc);
      
      if (filter === 'upcoming') {
        return meeting.status === 'confirmed' && meetingTime > now;
      } else if (filter === 'past') {
        return meetingTime < now;
      } else if (filter === 'cancelled') {
        return meeting.status === 'cancelled';
      }
      return true;
    }).sort((a, b) => new Date(a.startUtc) - new Date(b.startUtc));
  };

  const isUpcoming = (meeting) => {
    const meetingTime = new Date(meeting.startUtc);
    const now = new Date();
    const hoursDiff = (meetingTime - now) / (1000 * 60 * 60);
    return hoursDiff <= 24 && hoursDiff > 0;
  };

  const canJoin = (meeting) => {
    const meetingTime = new Date(meeting.startUtc);
    const now = new Date();
    const minutesDiff = (meetingTime - now) / (1000 * 60);
    // Can join 15 minutes before to 1 hour after
    return minutesDiff >= -15 && minutesDiff <= 60;
  };

  const filteredMeetings = filterMeetings();

  if (loading) {
    if (embedded) {
      return (
        <div className="flex items-center justify-center py-20">
          <p className="text-slate-600">Loading meetings...</p>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
        <Navigation />
        <div className="flex items-center justify-center py-20">
          <p className="text-slate-600">Loading meetings...</p>
        </div>
      </div>
    );
  }

  const content = (
    <>
      {!embedded && (
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            My Meetings
          </h1>
          <p className="text-slate-600">Manage your scheduled appointments</p>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6">
          <Button
            variant={filter === 'upcoming' ? 'default' : 'outline'}
            onClick={() => setFilter('upcoming')}
            className={filter === 'upcoming' ? 'bg-slate-900' : ''}
          >
            <Clock className="w-4 h-4 mr-2" />
            Upcoming
          </Button>
          <Button
            variant={filter === 'past' ? 'default' : 'outline'}
            onClick={() => setFilter('past')}
            className={filter === 'past' ? 'bg-slate-900' : ''}
          >
            <Calendar className="w-4 h-4 mr-2" />
            Past
          </Button>
          <Button
            variant={filter === 'cancelled' ? 'default' : 'outline'}
            onClick={() => setFilter('cancelled')}
            className={filter === 'cancelled' ? 'bg-slate-900' : ''}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Cancelled
          </Button>
        </div>

        {/* Meetings List */}
        {filteredMeetings.length === 0 ? (
          <Card className="p-12 text-center bg-white">
            <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              {filter === 'upcoming' && 'No upcoming meetings'}
              {filter === 'past' && 'No past meetings'}
              {filter === 'cancelled' && 'No cancelled meetings'}
            </h3>
            <p className="text-slate-600 mb-6">
              {filter === 'upcoming' && 'Share your DigiCalendar link with others to start receiving bookings'}
              {filter === 'past' && 'Your completed meetings will appear here'}
              {filter === 'cancelled' && 'Cancelled meetings will appear here'}
            </p>
            {filter === 'upcoming' && !embedded && (
              <Button 
                onClick={() => navigate('/settings')}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Set Up Your Availability
              </Button>
            )}
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredMeetings.map((meeting, idx) => {
              const isHost = meeting.hostId === currentUser?.id;
              const otherPerson = isHost ? meeting.clientName : meeting.hostName;
              const meetingTime = dayjs(meeting.startUtc);
              const isUpcomingSoon = isUpcoming(meeting);
              const canJoinNow = canJoin(meeting);

              return (
                <Card key={meeting.id || idx} className="p-6 bg-white hover:shadow-lg transition-shadow">
                  {isUpcomingSoon && meeting.status === 'confirmed' && (
                    <div className="mb-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                      <div className="flex items-center gap-2 text-yellow-800">
                        <AlertCircle className="w-5 h-5" />
                        <span className="font-semibold">Upcoming in {meetingTime.fromNow(true)}</span>
                      </div>
                    </div>
                  )}

                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="flex items-center justify-center w-12 h-12 rounded-full bg-slate-200">
                        <User className="w-6 h-6 text-slate-500" />
                      </div>
                      
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-slate-900 mb-1">
                          Meeting with {otherPerson}
                        </h3>
                        <div className="space-y-2 text-sm text-slate-600">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>{meetingTime.format('dddd, MMMM D, YYYY')}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            <span>{meetingTime.format('h:mm A')} ({meetingTime.fromNow()})</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Video className="w-4 h-4" />
                            <span>Duration: {meeting.duration || 30} minutes</span>
                          </div>
                        </div>

                        {meeting.status === 'cancelled' && (
                          <div className="mt-3 inline-block px-3 py-1 bg-red-100 text-red-800 text-sm rounded-full">
                            Cancelled
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    {meeting.status === 'confirmed' && (
                      <div className="flex flex-col gap-2 ml-4">
                        {canJoinNow && (
                          <Button
                            onClick={() => handleJoinMeeting(meeting.meetingRoomId)}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <Video className="w-4 h-4 mr-2" />
                            Join Now
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          onClick={() => handleRescheduleMeeting(meeting.id, meeting.hostId)}
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Reschedule
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleCancelMeeting(meeting.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      <Navigation />
      <div className="max-w-5xl mx-auto px-4 py-8">
        {content}
      </div>
    </div>
  );
};

export default MyMeetings;
